/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  computed,
  effect,
  ElementRef,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  TemplateRef,
  untracked,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { finalize, map, Observable, Subject, takeUntil } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import {
  ActionDefinition,
  ResponsiveTableColumn,
  ServerSideDataFetcher,
} from './responsive-table.models';
import {
  CustomInputComponent,
  CustomMultiSelectComponent,
  CustomSelectComponent,
  CustomDateInputComponent,
  CustomButtonComponent,
  CustomSwitchComponent,
  SelectOption,
} from '../../../public-api';
import { BaseCrudService, PaginatedResponse } from '../../services/base-crud.service';
import { PermissionsService } from '../../services/permissions.service';

/**
 * Interfaz para respuestas de datos genéricas
 */
interface DataResponse<T> {
  items: T[];
  total: number;
}

@Component({
  selector: 'lib-responsive-table',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CustomInputComponent,
    CustomSelectComponent,
    CustomMultiSelectComponent,
    CustomDateInputComponent,
    CustomButtonComponent,
    CustomSwitchComponent,
    TranslateModule,
  ],
  templateUrl: './responsive-table.component.html',
})
export class ResponsiveTableComponent implements OnInit, OnDestroy {
  @ViewChild('tableTitleElement') tableTitleEl!: ElementRef;
  private _destroy$ = new Subject<void>();
  title = input('');
  titleClass = input('text-[24px]');
  btnLabel = input('');
  loading = signal(false);
  btnBack = input(false);
  showClearFiltersButton = signal(false);
  flat = input(false);
  items = input<any[]>([]);
  itemsPerPage = input(25);
  private _itemsPerPage = signal(25);
  columns = input<ResponsiveTableColumn[]>([]);
  itemTemplate = input<TemplateRef<any> | null>(null);
  createClicked = output<void>();
  showCreateButton = input<boolean>(true);
  actionDefinitions = input<ActionDefinition[]>([]);
  actionsColumnLabel = input<string>('');
  actionClicked = output<{ actionKey: string; item: any }>();
  showLimitItems = input<SelectOption[]>([
    { value: 5, display: '5' },
    { value: 10, display: '10' },
    { value: 25, display: '25' },
    { value: 50, display: '50' },
    { value: 100, display: '100' },
  ]);
  filtersChanged = output<{
    filters: Record<string, string | number | boolean | (string | number | boolean)[]>;
    page: number;
    itemsPerPage: number;
    orderBy?: string;
    orderDirection?: string;
  }>();

  // --- Sort State ---
  _sortState = signal<{ key: string; direction: 'asc' | 'desc' } | null>(null);

  cardsGrid = input<string>(
    'grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4'
  );

  // --- Row Selection Inputs ---
  rowSelectable = input<boolean>(false);
  rowIdKey = input<string>('id');
  selectionChanged = output<{ allSelected: boolean; included: any[]; excluded: any[] }>();

  // --- Row Selection State ---
  // Client-side mode: Simple array of selected items.
  selectedRows = signal<any[]>([]);

  // Server-side mode: More complex state.
  isAllSelected = signal(false); // Tracks if the 'select all' action was triggered.
  includedRows = signal<any[]>([]); // Individually selected rows when isAllSelected is false.
  excludedRows = signal<any[]>([]); // Deselected rows when isAllSelected is true.

  // --- Server-Side Loading Inputs ---
  serverSideLoad = input<boolean>(false);
  // Controla si los filtros se comportan como en modo servidor incluso en modo cliente
  serverSideFiltering = input<boolean>(false);
  dataFetcher = input<ServerSideDataFetcher<any> | null>(null);
  crudService = input<BaseCrudService<any> | null>(null);
  fetchOnInit = input<boolean>(true);
  private _initialFetchDone = false;

  // --- Internal State for Server-Side Loading ---
  private _serverSideItems = signal<any[]>([]);
  private _serverSideTotalItems = signal(0);
  private _serverSideCurrentPage = signal(1);

  // --- Client-Side State ---
  private _clientSideCurrentPage = signal(1);
  private _allItems = signal<any[]>([]);
  private _filteredItems = signal<any[]>([]);

  public filterValues = signal<{ [key: string]: any }>({});
  isFilterRowVisible = signal(true);

  isMobileFiltersExpanded = signal(false);
  hasPendingServerFilters = signal(false);

  createPermission = input<string[][]>();

  readonly shouldShowFilterRow = computed(() => {
    return this.columns().some((column) => column.filterType && column.filterType !== 'none');
  });

  paginationPages = computed(() => {
    const total = this.totalPages();
    const current = this.effectiveCurrentPage();
    const range = 2; // How many pages to show around the current page
    const pages: (number | string)[] = [];

    if (total <= 1) {
      return []; // No pagination needed for 1 or 0 pages
    }

    // Always show the first page
    pages.push(1);

    // Ellipsis after the first page if needed
    if (current > range + 2) {
      pages.push('...');
    }

    // Pages around the current page
    for (let i = Math.max(2, current - range); i <= Math.min(total - 1, current + range); i++) {
      pages.push(i);
    }

    // Ellipsis before the last page if needed
    if (current < total - range - 1) {
      pages.push('...');
    }

    // Always show the last page
    pages.push(total);

    return pages;
  });

  paginatedItems = computed(() => {
    if (this.serverSideLoad()) {
      return this._serverSideItems();
    }

    // Para el modo cliente, paginar los items filtrados
    const start = (this._clientSideCurrentPage() - 1) * this._itemsPerPage();
    const end = start + this._itemsPerPage();
    return this._filteredItems().slice(start, end);
  });

  totalPages = computed(() => {
    if (this.serverSideLoad()) {
      return Math.ceil(this._serverSideTotalItems() / this._itemsPerPage());
    }
    // Para el modo cliente, usar los items filtrados
    // Los filtros ya están aplicados en _filteredItems
    const filteredItemsCount = this._filteredItems().length;

    return Math.max(1, Math.ceil(filteredItemsCount / this._itemsPerPage()));
  });

  // Computed property para acceder a los items actuales según el modo
  effectiveItems = computed(() => {
    if (this.serverSideLoad()) {
      return this._serverSideItems();
    } else {
      return this._filteredItems();
    }
  });

  effectiveCurrentPage = computed(() => {
    if (this.serverSideLoad()) {
      return this._serverSideCurrentPage();
    }
    // Asegurar que la página actual no excede el total de páginas
    const currentPage = this._clientSideCurrentPage();
    const maxPage = this.totalPages();
    return currentPage > maxPage && maxPage > 0 ? maxPage : currentPage;
  });

  onLimitItemsPage(size: number): void {
    this._itemsPerPage.set(size);

    if (this.serverSideLoad()) {
      this._serverSideCurrentPage.set(1);
    } else {
      this._clientSideCurrentPage.set(1);
      this._scrollToTitleIfMobile();
    }
    this.filtersChanged.emit({
      filters: this._getSanitizedFilters(),
      page: this.effectiveCurrentPage(),
      itemsPerPage: this._itemsPerPage(),
      orderBy: this._getOrderByString(),
      orderDirection: this._getDirectionByString(),
    });
  }

  onNextPage(): void {
    if (this.serverSideLoad() && this._serverSideCurrentPage() < this.totalPages()) {
      this._serverSideCurrentPage.update((p) => p + 1);
    } else if (!this.serverSideLoad() && this._clientSideCurrentPage() < this.totalPages()) {
      this._clientSideCurrentPage.update((p) => p + 1);
      this._scrollToTitleIfMobile();
    }
    this.filtersChanged.emit({
      filters: this._getSanitizedFilters(),
      page: this.effectiveCurrentPage(),
      itemsPerPage: this._itemsPerPage(),
      orderBy: this._getOrderByString(),
      orderDirection: this._getDirectionByString(),
    });
  }

  onPreviousPage(): void {
    if (this.serverSideLoad() && this._serverSideCurrentPage() > 1) {
      this._serverSideCurrentPage.update((p) => p - 1);
    } else if (!this.serverSideLoad() && this._clientSideCurrentPage() > 1) {
      this._clientSideCurrentPage.update((p) => p - 1);
      this._scrollToTitleIfMobile();
    }
    this.filtersChanged.emit({
      filters: this._getSanitizedFilters(),
      page: this.effectiveCurrentPage(),
      itemsPerPage: this._itemsPerPage(),
      orderBy: this._getOrderByString(),
      orderDirection: this._getDirectionByString(),
    });
  }

  goToPage(page: number | string): void {
    if (typeof page === 'string') return; // Skip ellipsis or other non-number values
    if (this.serverSideLoad()) {
      this._serverSideCurrentPage.set(page);
    } else {
      this._clientSideCurrentPage.set(page);
      this._scrollToTitleIfMobile();
    }
    this.filtersChanged.emit({
      filters: this._getSanitizedFilters(),
      page: this.effectiveCurrentPage(),
      itemsPerPage: this._itemsPerPage(),
      orderBy: this._getOrderByString(),
      orderDirection: this._getDirectionByString(),
    });
  }

  clearSelection(): void {
    this.selectedRows.set([]);
    this.isAllSelected.set(false);
    this.includedRows.set([]);
    this.excludedRows.set([]);
  }

  excludeFiltersFromClear = input<string[]>([]);

  clearFilters(): void {
    this.clearSelection();
    const currentFilters = this.filterValues();
    const excludedKeys = this.excludeFiltersFromClear();
    const newFilters: Record<string, any> = {};

    excludedKeys.forEach((key) => {
      if (currentFilters[key] !== undefined) {
        newFilters[key] = currentFilters[key];
      }
    });

    this.filterValues.set(newFilters);
    this.applyFilters();
  }

  toggleFilterRowVisibility(): void {
    this.isFilterRowVisible.update((value) => !value);
  }

  toggleMobileFilters(): void {
    this.isMobileFiltersExpanded.update((value) => !value);
  }

  /**
   * Applies filters for server-side pagination or client-side with server-side filtering behavior.
   * For regular client-side, filtering is automatic via signals.
   */
  onSortColumn(key: string): void {
    const current = this._sortState();
    if (current && current.key === key) {
      // Toggle direction
      this._sortState.set({ key, direction: current.direction === 'asc' ? 'desc' : 'asc' });
    } else {
      this._sortState.set({ key, direction: 'asc' });
    }

    this.filtersChanged.emit({
      filters: this._getSanitizedFilters(),
      page: 1,
      itemsPerPage: this._itemsPerPage(),
      orderBy: this._getOrderByString(),
      orderDirection: this._getDirectionByString(),
    });

    if (this.serverSideLoad()) {
      // Si ya estamos en página 1, el efecto no se dispara (mismo valor),
      // así que llamamos _loadData directamente. Si estamos en otra página,
      // el set(1) dispara el efecto automáticamente — no llamamos _loadData aquí.
      if (this._serverSideCurrentPage() === 1) {
        this._loadData({
          page: 1,
          itemsPerPage: this._itemsPerPage(),
          filters: this.filterValues(),
        });
      } else {
        this._serverSideCurrentPage.set(1); // efecto se encarga del _loadData
      }
    } else {
      this._clientSideCurrentPage.set(1);
      if (!this.serverSideFiltering()) {
        this._applyClientSideSort();
      }
    }
  }

  private _getOrderByString(): string | undefined {
    const sort = this._sortState();
    return sort ? sort.key : undefined;
  }

  private _getDirectionByString(): string | undefined {
    const sort = this._sortState();
    return sort ? sort.direction : undefined;
  }

  private _applyClientSideSort(): void {
    const sort = this._sortState();
    if (!sort) return;

    const sorted = [...this._filteredItems()].sort((a, b) => {
      const aVal = this.getNestedProperty(a, sort.key);
      const bVal = this.getNestedProperty(b, sort.key);

      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sort.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }

      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      if (aStr < bStr) return sort.direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return sort.direction === 'asc' ? 1 : -1;
      return 0;
    });

    this._filteredItems.set(sorted);
  }

  applyFilters(): void {
    this._initialFetchDone = true;
    this.filtersChanged.emit({
      filters: this.filterValues(),
      page: this.effectiveCurrentPage(),
      itemsPerPage: this._itemsPerPage(),
      orderBy: this._getOrderByString(),
      orderDirection: this._getDirectionByString(),
    });
    if (this.serverSideLoad() || this.serverSideFiltering()) {
      this.clearSelection();
      this.hasPendingServerFilters.set(false);

      if (this.serverSideLoad()) {
        // Modo servidor - manejo de paginación
        // Si ya estamos en la página 1, necesitamos forzar una recarga de los datos
        if (this._serverSideCurrentPage() === 1) {
          // Cargar datos directamente para forzar la actualización
          const page = 1;
          const itemsPerPage = this._itemsPerPage();
          const filters = this.filterValues();
          this._loadData({
            page,
            itemsPerPage,
            filters,
          });
        } else {
          // Si no estamos en la página 1, simplemente reiniciamos la página
          // lo que disparará el efecto de carga automáticamente
          this._serverSideCurrentPage.set(1);
        }
      } else {
        // Modo cliente con filtrado tipo servidor
        // Forzar una recarga completa de los datos con los nuevos filtros
        const filters = this.filterValues();
        this._loadData({
          filters,
          isRefresh: true,
        });
      }
    } else {
      // En modo cliente normal, simplemente aplicamos los filtros localmente
      this._applyClientSideFilters();
    }
  }

  /**
   * Aplica los filtros al dataset en modo cliente
   * @private
   */
  private _applyClientSideFilters(): void {
    const allItems = this._allItems();
    const filters = this.filterValues();

    // Si no hay filtros activos, mostrar todos los elementos
    if (!this._hasActiveFilters(filters)) {
      this._filteredItems.set(allItems);
      return;
    }

    // Aplicar todos los filtros activos
    const filteredItems = allItems.filter((item) => {
      return Object.keys(filters).every((key) => {
        const filterValue = filters[key];
        // Ignorar filtros vacíos
        if (filterValue === null || filterValue === undefined || filterValue === '') {
          return true;
        }

        const itemValue = item[key];
        // Buscar coincidencia según el tipo de dato
        if (typeof filterValue === 'string') {
          // Para cadenas, buscar coincidencia parcial
          return String(itemValue).toLowerCase().includes(String(filterValue).toLowerCase());
        } else {
          // Para otros tipos, buscar coincidencia exacta
          return itemValue === filterValue;
        }
      });
    });

    this._filteredItems.set(filteredItems);
    this._clientSideCurrentPage.set(1); // Reset a la primera página
  }

  /**
   * Verifica si hay filtros activos
   * @private
   */
  private _hasActiveFilters(filters: { [key: string]: any }): boolean {
    const excludedKeys = this.excludeFiltersFromClear();
    return Object.keys(filters).some(
      (key) =>
        !excludedKeys.includes(key) &&
        filters[key] !== null &&
        filters[key] !== '' &&
        filters[key] !== undefined &&
        (!Array.isArray(filters[key]) || filters[key].length > 0)
    );
  }

  onFilterChange(key: string, value: any): void {
    const filters = { ...this.filterValues() };

    if (value === null || value === undefined || value === '') {
      delete filters[key];
    } else {
      filters[key] = value;
    }
    this.filterValues.set(filters);

    if (!this.serverSideLoad() && !this.serverSideFiltering()) {
      // En modo cliente sin filtrado tipo servidor, aplicar filtros inmediatamente
      this._applyClientSideFilters();
    } else {
      // En modo servidor o con filtrado tipo servidor, marcar que hay filtros pendientes
      this.hasPendingServerFilters.set(true);
    }
  }

  onItemsPerPageChange(size: number) {
    this._itemsPerPage.set(size);
    if (this.serverSideLoad()) {
      this._serverSideCurrentPage.set(1); // Go to first page, which triggers refetch
    } else {
      this._clientSideCurrentPage.set(1);
    }
  }

  emitCreate() {
    this.createClicked.emit();
  }

  onGenericActionClick(actionKey: string, item: any): void {
    this.actionClicked.emit({ actionKey, item });
  }

  /**
   * Public method to allow parent components to force a refresh of the table data.
   * This can be useful after CRUD operations that affect the data displayed in the table.
   */
  public refreshData(): void {
    this._initialFetchDone = true;
    if (this.serverSideLoad()) {
      // Clear current items to make the refresh visually noticeable
      this._serverSideItems.set([]);

      // Force data reload with current parameters
      const page = this._serverSideCurrentPage();
      const itemsPerPage = this._itemsPerPage();
      const filters = untracked(this.filterValues);

      // Directly call the load method to bypass the effect dependency chain
      this._loadData({
        page,
        itemsPerPage,
        filters,
        isRefresh: true,
      });
    } else {
      console.warn('refreshData() is primarily for server-side loaded tables.');
      // Podríamos también recargar los datos del cliente si es necesario
      this._loadData({ isRefresh: true });
    }
  }

  // Muestra el estado actual de la vista (desktop o mobile)
  isDesktop = signal(false);
  private _mediaQueryList: MediaQueryList | undefined;
  private _mediaQueryListener: (() => void) | undefined;

  // Para el modo de visualización manual
  manualViewMode = input<'auto' | 'table' | 'cards'>('auto');
  // Signal interno para manejar cambios que se inicializa con el valor del input
  private _viewMode = signal<'auto' | 'table' | 'cards'>('auto');

  // Indica si se debe mostrar un botón para alternar entre vistas
  allowViewModeToggle = input<boolean>(false);

  // Método para cambiar entre vistas de tabla y tarjetas
  toggleViewMode(): void {
    if (this.effectiveViewMode() === 'table') {
      this._viewMode.set('cards');
    } else {
      this._viewMode.set('table');
    }
  }

  // Determina efectivamente qué vista se debe mostrar
  effectiveViewMode = computed(() => {
    // Si el modo manual está en auto, usar el responsive automático
    if (this._viewMode() === 'auto') {
      return this.isDesktop() ? 'table' : 'cards';
    }
    // Si no, usar el modo manual seleccionado
    return this._viewMode();
  });

  // Determina si se debe usar la vista de tabla
  useTableView = computed(() => this.effectiveViewMode() === 'table');

  // Determina si se debe usar la vista de cards
  useCardsView = computed(() => this.effectiveViewMode() === 'cards');

  areAllCurrentPageRowsSelected = computed(() => {
    const currentPageItems = this.effectiveItems();
    if (currentPageItems.length === 0) return false;
    return currentPageItems.every((item) => this.isRowSelected(item));
  });

  isRowSelected(item: any): boolean {
    const idKey = this.rowIdKey();
    if (this.serverSideLoad()) {
      if (this.isAllSelected()) {
        return !this.excludedRows().some((row) => row[idKey] === item[idKey]);
      } else {
        return this.includedRows().some((row) => row[idKey] === item[idKey]);
      }
    } else {
      return this.selectedRows().some((row) => row[idKey] === item[idKey]);
    }
  }

  toggleRowSelection(item: any): void {
    const idKey = this.rowIdKey();
    const isSelected = this.isRowSelected(item);

    if (this.serverSideLoad()) {
      if (this.isAllSelected()) {
        // If all are selected, toggling a row adds/removes it from the exclusion list
        if (isSelected) {
          this.excludedRows.update((rows) => [...rows, item]);
        } else {
          this.excludedRows.update((rows) => rows.filter((row) => row[idKey] !== item[idKey]));
        }
      } else {
        // If not all are selected, toggling a row adds/removes it from the inclusion list
        if (isSelected) {
          this.includedRows.update((rows) => rows.filter((row) => row[idKey] !== item[idKey]));
        } else {
          this.includedRows.update((rows) => [...rows, item]);
        }
      }
    } else {
      // Client-side logic remains simple
      if (isSelected) {
        this.selectedRows.update((rows) => rows.filter((row) => row[idKey] !== item[idKey]));
      } else {
        this.selectedRows.update((rows) => [...rows, item]);
      }
    }
  }

  toggleSelectAll(): void {
    if (this.serverSideLoad()) {
      const newSelectAllState = !this.isAllSelected();
      this.isAllSelected.set(newSelectAllState);
      this.includedRows.set([]);
      this.excludedRows.set([]);
    } else {
      // Client-side: select all items from the source array
      const allSelected = this.selectedRows().length === this.items().length;
      if (allSelected) {
        this.selectedRows.set([]);
      } else {
        this.selectedRows.set([...this.items()]);
      }
    }
  }

  headerCheckboxState = computed(() => {
    if (this.serverSideLoad()) {
      if (this.isAllSelected() && this.excludedRows().length === 0) {
        return { checked: true, indeterminate: false }; // All selected
      }
      if (this.isAllSelected() && this.excludedRows().length > 0) {
        return { checked: false, indeterminate: true }; // All selected, but some excluded
      }
      if (!this.isAllSelected() && this.includedRows().length > 0) {
        return { checked: false, indeterminate: true }; // Some individually selected
      }
      return { checked: false, indeterminate: false }; // None selected
    } else {
      const numSelected = this.selectedRows().length;
      const numTotal = this.items().length;
      if (numSelected === 0) {
        return { checked: false, indeterminate: false };
      }
      if (numSelected === numTotal) {
        return { checked: true, indeterminate: false };
      }
      return { checked: false, indeterminate: true };
    }
  });

  ngOnInit(): void {
    // Inicialización de datos
    if (this.items && this.items().length > 0) {
      // Si hay datos proporcionados por input, usarlos para modo cliente
      if (!this.serverSideLoad()) {
        this._allItems.set(this.items());
        this._applyClientSideFilters();
      }
    } else if (!this.serverSideLoad() && (this.crudService() || this.dataFetcher())) {
      // Carga inicial para modo cliente, ejecutar inmediatamente
      this._loadData({});
    }
    // El modo servidor se maneja automáticamente por el efecto
  }

  constructor(public permissionsService: PermissionsService) {
    // Mantener todas las páginas y filtros sincronizados
    effect(() => {
      const itemsPerPage = this.itemsPerPage();
      this._itemsPerPage.set(itemsPerPage);

      // Si estamos en modo cliente, aplicar filtros cada vez que cambian los valores
      if (!this.serverSideLoad() && this.items().length > 0) {
        // Si los ítems cambian, reajustar el estado
        this._allItems.set(this.items());
        this._applyClientSideFilters();
      }
    });

    // Detectar cambios en filtros y aplicarlos automáticamente en modo cliente
    effect(() => {
      // Ignoramos este efecto si estamos en modo servidor
      if (this.serverSideLoad()) {
        const _ = this.filterValues(); // Read the signal to subscribe to changes
        this.hasPendingServerFilters.set(true);
        return;
      }

      // Solo aplicar en modo cliente cuando tenemos datos
      if (this._allItems().length > 0) {
        this._applyClientSideFilters();
      }
    });

    // Efecto para manejar la paginación
    effect(() => {
      // Si estamos en modo servidor, necesitamos recargar los datos al cambiar de página
      if (this.serverSideLoad()) {
        // Obtenemos los valores de la página y tamaño de página actuales
        const page = this._serverSideCurrentPage();
        const itemsPerPage = this._itemsPerPage();
        const filters = untracked(this.filterValues);

        if (!this.fetchOnInit() && !this._initialFetchDone) {
          return;
        }

        // Llamar al método unificado de carga de datos
        this._loadData({
          page,
          itemsPerPage,
          filters,
        });
      }
    });

    // Efecto para sincronizar manualViewMode con _viewMode
    effect(() => {
      // Cuando cambia manualViewMode desde el exterior, actualizar _viewMode
      this._viewMode.set(this.manualViewMode());
    });

    // Media query listener for responsive design
    if (typeof window !== 'undefined' && window.matchMedia) {
      this._mediaQueryList = window.matchMedia('(min-width: 768px)');
      this._mediaQueryListener = () => this.isDesktop.set(this._mediaQueryList!.matches);
      this._mediaQueryList.addEventListener('change', this._mediaQueryListener);
      this.isDesktop.set(this._mediaQueryList.matches);
    } else {
      this.isDesktop.set(true);
    }
  }

  /**
   * Método unificado para cargar datos en modo servidor o cliente
   * @param options Opciones para la carga de datos
   * @private
   */
  private _loadData(options: {
    page?: number;
    itemsPerPage?: number;
    filters?: { [key: string]: any };
    isRefresh?: boolean;
  }): void {
    this._initialFetchDone = true;
    const isServerMode = this.serverSideLoad();
    const service = this.crudService();
    const fetcher = this.dataFetcher();

    if (!service && !fetcher) return;

    // Default values
    const {
      page = isServerMode ? this._serverSideCurrentPage() : 1,
      itemsPerPage = this._itemsPerPage(),
      filters = this.filterValues(),
      isRefresh = false,
    } = options;

    if (isRefresh) {
      if (isServerMode) {
        this._serverSideItems.set([]);
      } else {
        this._filteredItems.set([]);
      }
    }

    // Actualizar la visibilidad del botón de borrar filtros
    // Solo mostramos el botón si hay filtros activos y se está ejecutando una carga de datos
    this.showClearFiltersButton.set(this._hasActiveFilters(filters));

    this.loading.set(true);
    let dataObservable$: Observable<DataResponse<any>>;

    // Merge orderBy from sort state into filters so every load path sends it to the backend
    // untracked() prevents the pagination effect from re-firing when _sortState changes
    const sortState = untracked(this._sortState);
    const effectiveFilters = sortState
      ? {
          ...filters,
          orderBy: sortState.key,
          orderDirection: sortState.direction,
        }
      : { ...filters };

    if (isServerMode) {
      if (service) {
        dataObservable$ =
          Object.keys(effectiveFilters).length == 0
            ? service.getPaged(page, itemsPerPage).pipe(
                map((response: PaginatedResponse<any>) => ({
                  items: response.results,
                  total: response.count,
                }))
              )
            : service.getPagedFilter(page, itemsPerPage, effectiveFilters).pipe(
                map((response: PaginatedResponse<any>) => ({
                  items: response.results,
                  total: response.count,
                }))
              );
      } else if (fetcher) {
        dataObservable$ = fetcher({ page, itemsPerPage, filters: effectiveFilters });
      } else {
        this.loading.set(false);
        return;
      }

      dataObservable$
        .pipe(
          takeUntil(this._destroy$),
          finalize(() => this.loading.set(false))
        )
        .subscribe({
          next: (response: DataResponse<any>) => {
            this._serverSideItems.set(response.items);
            this._serverSideTotalItems.set(response.total);
            this.hasPendingServerFilters.set(false);
          },
          error: (err) => {
            console.error('Error loading server-side data:', err);
            this._serverSideItems.set([]);
            this._serverSideTotalItems.set(0);
          },
        });
    } else {
      // Modo cliente
      if (service) {
        dataObservable$ = service.getAll().pipe(map((items) => ({ items, total: items.length })));
      } else if (fetcher) {
        dataObservable$ = fetcher({ page: 1, itemsPerPage: 1000, filters });
      } else {
        this.loading.set(false);
        return;
      }

      dataObservable$
        .pipe(
          takeUntil(this._destroy$),
          finalize(() => this.loading.set(false))
        )
        .subscribe({
          next: (response: DataResponse<any>) => {
            this._allItems.set(response.items);
            this._applyClientSideFilters();
          },
          error: (err) => {
            console.error('Error loading client-side data:', err);
            this._allItems.set([]);
            this._filteredItems.set([]);
          },
        });
    }
  }

  ngOnDestroy(): void {
    if (this._mediaQueryList && this._mediaQueryListener) {
      this._mediaQueryList.removeEventListener('change', this._mediaQueryListener);
    }
    this._destroy$.next();
    this._destroy$.complete();
  }

  private _scrollToTitleIfMobile(): void {
    // Ensure this runs after the view is stable, timeout helps.
    setTimeout(() => {
      if (!this.isDesktop() && this.tableTitleEl) {
        this.tableTitleEl.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 0);
  }

  getValidCurrencyCode(currencyCode?: string): string {
    const validCurrencies = ['USD', 'MXN'];
    return currencyCode && validCurrencies.includes(currencyCode) ? currencyCode : 'USD';
  }

  /**
   * Accede de forma segura a propiedades anidadas de un objeto usando notación de puntos
   * Ejemplo: 'user.profile.name' accederá a item.user.profile.name si existe
   * @param item El objeto del cual extraer la propiedad
   * @param key La clave con notación de puntos (ejemplo: 'user.name')
   * @returns El valor de la propiedad o undefined si no existe
   */
  getNestedProperty(item: any, key: string): any {
    if (!item || !key) return undefined;

    // Si la clave no contiene puntos, accedemos directamente
    if (!key.includes('.')) return item[key];

    // Si contiene puntos, navegamos por la estructura del objeto
    return key.split('.').reduce((obj, prop) => {
      return obj && typeof obj === 'object' ? obj[prop] : undefined;
    }, item);
  }

  private _getSanitizedFilters(): Record<
    string,
    string | number | boolean | (string | number | boolean)[]
  > {
    const filters = this.filterValues();

    const sanitized: Record<string, string | number | boolean | (string | number | boolean)[]> = {};

    Object.entries(filters).forEach(([key, value]) => {
      if (
        value === null ||
        value === undefined ||
        value === '' ||
        (Array.isArray(value) && value.length === 0)
      ) {
        return;
      }

      // boolean
      if (typeof value === 'boolean') {
        sanitized[key] = value;
        return;
      }

      // number
      if (typeof value === 'number') {
        sanitized[key] = value;
        return;
      }

      // string
      if (typeof value === 'string') {
        sanitized[key] = value;
        return;
      }

      // array permitido
      if (Array.isArray(value)) {
        sanitized[key] = value as (string | number | boolean)[];
      }
    });

    return sanitized;
  }

  goBack() {
    history.back();
  }
}
